import type {
  AccessRevocationDialogState,
  AccessRevocationPayload,
} from '@/stores/accessRevocationStore';
import { useAccessRevocationStore } from '@/stores/accessRevocationStore';

const PROJECT_ROUTE_RE = /^\/project\/([^/?#]+)(?:\/|$)/;
const PROJECT_QUERY_KEYS = ['parentProjectId', 'projectId', 'legacyProjectId'];
export const PARSE_FLOW_PATH = '/project/parse-flow';

/** 经典编辑器路由 `/project/:projectId`（不含 parse-flow、ai-workspace 等静态页）。 */
export function getClassicProjectRouteProjectId(pathname: string): string | null {
  const match = pathname.match(PROJECT_ROUTE_RE);
  if (!match?.[1] || match[1] === 'parse-flow' || match[1] === 'ai-workspace') return null;
  return match[1];
}

export function isOnParseFlowPage(pathname: string): boolean {
  return pathname === PARSE_FLOW_PATH || pathname.endsWith('/parse-flow');
}

/** 分镜页父项目仍在时，子集会 404 应留在本页改选，不要整页踢回工作台。 */
export function isParseFlowRecoverableSubprojectMiss(input: {
  statusCode?: number;
  pathname: string;
  search: string;
  projectId?: string | null;
}): boolean {
  if (input.statusCode !== 404) return false;
  if (!isOnParseFlowPage(input.pathname)) return false;
  const parentId = new URLSearchParams(input.search).get('parentProjectId');
  const projectId = input.projectId?.trim();
  if (!parentId || !projectId) return false;
  return projectId !== parentId;
}

/** Parse Flow 当前编辑的子项目/项目 id（query projectId 或 legacy 路径段）。 */
export function getParseFlowFocusedProjectId(
  pathname: string,
  search: string,
  fallbackProjectId?: string | null
): string | null {
  if (!isOnParseFlowPage(pathname)) return null;
  if (pathname === PARSE_FLOW_PATH) {
    const params = new URLSearchParams(search);
    return params.get('projectId') ?? fallbackProjectId ?? null;
  }
  return getClassicProjectRouteProjectId(pathname) ?? fallbackProjectId ?? null;
}

/** 仅当前路由真正打开项目时才观察详情，避免 persist 的 currentProjectId 在工作区误打请求。 */
export function resolveObservedProjectId(
  pathname: string,
  search: string,
  persistedProjectId?: string | null
): string | null {
  const routeProjectId = getClassicProjectRouteProjectId(pathname);
  if (routeProjectId) return routeProjectId;
  return getParseFlowFocusedProjectId(pathname, search, persistedProjectId);
}

export function matchesParseFlowProjectNotification(
  pathname: string,
  search: string,
  relatedProjectIds: string[],
  fallbackProjectId?: string | null
): boolean {
  if (!isOnParseFlowPage(pathname)) return false;
  const focusedProjectId = getParseFlowFocusedProjectId(pathname, search, fallbackProjectId);
  if (focusedProjectId && relatedProjectIds.includes(focusedProjectId)) {
    return true;
  }
  const locationIds = getProjectIdsFromLocation(pathname, search);
  if (fallbackProjectId) locationIds.add(fallbackProjectId);
  return relatedProjectIds.some((id) => locationIds.has(id));
}

export function getProjectIdsFromLocation(pathname: string, search: string): Set<string> {
  const ids = new Set<string>();
  const match = pathname.match(PROJECT_ROUTE_RE);
  if (match?.[1] && match[1] !== 'parse-flow' && match[1] !== 'ai-workspace') {
    ids.add(match[1]);
  }

  const params = new URLSearchParams(search);
  for (const key of PROJECT_QUERY_KEYS) {
    const value = params.get(key);
    if (value) ids.add(value);
  }
  return ids;
}

export function matchesProjectAccessRevocation({
  pathname,
  search,
  projectId,
  affectedProjectIds,
  fallbackProjectIds,
}: {
  pathname: string;
  search: string;
  projectId?: string;
  affectedProjectIds?: string[];
  fallbackProjectIds?: string[];
}): boolean {
  const currentProjectIds = getProjectIdsFromLocation(pathname, search);
  if (currentProjectIds.size === 0 && pathname === PARSE_FLOW_PATH) {
    for (const id of fallbackProjectIds ?? []) {
      if (id) currentProjectIds.add(id);
    }
  }
  if (currentProjectIds.size === 0) return false;

  const revokedIds = new Set<string>();
  const params = new URLSearchParams(search);
  const parseFlowParentProjectId = isOnParseFlowPage(pathname)
    ? params.get('parentProjectId')
    : null;
  const parseFlowFocusedProjectId = getParseFlowFocusedProjectId(pathname, search);
  const shouldTreatAffectedProjectsAsRevokedTargets = Boolean(
    parseFlowFocusedProjectId &&
    parseFlowParentProjectId &&
    projectId === parseFlowParentProjectId &&
    affectedProjectIds?.length
  );

  if (projectId && !shouldTreatAffectedProjectsAsRevokedTargets) revokedIds.add(projectId);
  for (const id of affectedProjectIds ?? []) {
    if (id) revokedIds.add(id);
  }
  for (const id of revokedIds) {
    if (currentProjectIds.has(id)) return true;
  }
  return false;
}

/** Parse Flow 无改稿权限时，5 秒倒计时后返回授权单集编辑页或当前身份工作台 */
export function showParseFlowDeniedRedirectDialog(payload: {
  returnProjectId?: string;
  returnPath?: string;
  redirectTarget?: 'workspace' | 'project';
  projectName?: string;
  affectedProjectIds?: string[];
  title?: string;
  description?: string;
}): void {
  const redirectTarget =
    payload.redirectTarget ?? (payload.returnProjectId ? 'project' : 'workspace');
  useAccessRevocationStore.getState().show({
    kind: 'project',
    redirectTarget,
    returnProjectId: payload.returnProjectId,
    returnPath: payload.returnPath,
    projectId: payload.returnProjectId,
    projectName: payload.projectName,
    title: payload.title ?? '暂无智能分镜权限',
    description:
      payload.description ??
      (redirectTarget === 'project'
        ? '当前账号暂无改稿权限，无法继续使用智能分镜，5 秒后将自动返回项目制作页。'
        : '当前账号暂无改稿权限，无法继续使用智能分镜，5 秒后将自动返回工作台。'),
    affectedProjectIds:
      payload.affectedProjectIds ?? (payload.returnProjectId ? [payload.returnProjectId] : []),
  });
}

export function isAccessRevocationDuplicate(
  current: AccessRevocationDialogState,
  next: Pick<AccessRevocationPayload, 'kind' | 'orgId' | 'projectId' | 'affectedProjectIds'>
): boolean {
  if (!current.open || current.kind !== next.kind) return false;
  if (next.kind === 'org') return Boolean(next.orgId && current.orgId === next.orgId);

  const currentIds = new Set(current.affectedProjectIds);
  if (current.projectId) currentIds.add(current.projectId);
  const nextIds = new Set(next.affectedProjectIds ?? []);
  if (next.projectId) nextIds.add(next.projectId);
  for (const id of nextIds) {
    if (currentIds.has(id)) return true;
  }
  return false;
}
